import { Component, HostBinding, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { filter, map } from 'rxjs/operators'
import { NgxPermissionsService } from 'ngx-permissions'
import { AuthService, KEY_TOKEN } from '@tv3/services/auth.service'
import * as fromRoot from '@tv3/store/state'
import { Destroyable, DialogService, isSomething, LocalStorage, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { environment } from '@tv3/environments/environment'
import { ChangePasswordDialogService } from '@tv3/shared/change-password/change-password-dialog/change-password-dialog.service'
import { userRoleToFriendlyString } from '@tv3/utils/functions/user-role-to-string'
import { NgProgress } from 'ngx-progressbar'
import { Observable } from 'rxjs'
import { UserMfaDialogComponent } from '@tv3/containers/settings/user-settings/user-mfa-dialog/user-mfa-dialog.component'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { NavigationEnd, Router } from '@angular/router'
import { getLocale } from '@tv3/utils/functions/get-locale'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import * as moment from 'moment'

moment.locale('en')

const KEY_SIDEBAR_EXPANDED = 'sidebar_expanded'

type SidebarGroup = 'finances' | 'operations'

const SIDEBAR_GROUP_ROUTES: Record<SidebarGroup, string[]> = {
  finances: ['/invoices', '/charges', '/extras', '/expenses', '/formulas', '/discounts', '/security-deposits'],
  operations: ['/tasks', '/incidents', '/inventories', '/vendors'],
}

@Component({
  selector: 'app-sidebar2',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  host: { class: 'dashboard_sidebar' },
})
export class Sidebar2Component extends Destroyable implements OnInit {
  isLoading$: Observable<boolean>

  get username() {
    if (this.auth.user) {
      const firstName = this.auth.user.firstName
      const lastName = this.auth.user.lastName
      return `${firstName} ${lastName}`
    } else {
      return ''
    }
  }

  get user() {
    return this.auth.user
  }

  get role() {
    if (this.auth.user) {
      return userRoleToFriendlyString(this.auth.user.roles)
    } else {
      return ''
    }
  }

  languages: { name: string; value: string }[] = environment.languages
  currentLanguage: { name: string; value: string }
  readonly showSecurityDeposits = environment.showSecurityDeposits

  isLanguageSwitcherOpen = false
  isUserActionsMenuOpen = false
  isPaymentsMenuOpen = false
  isTasksMenuOpen = false

  @HostBinding('class.expanded') isExpanded = false

  @HostBinding('class.has-subnav') get hasSubnav() {
    return this.isExpanded && !!this.activeGroup
  }

  get activeGroup(): SidebarGroup | null {
    const url = this.router.url || ''
    const groups = Object.keys(SIDEBAR_GROUP_ROUTES) as SidebarGroup[]
    return groups.find((group) => SIDEBAR_GROUP_ROUTES[group].some((prefix) => url.startsWith(prefix))) || null
  }

  smartDeviceSub$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.SmartDevice)),
    map((res) => res || {})
  )

  constructor(
    private auth: AuthService,
    private router: Router,
    private storage: LocalStorage,
    private store: Store<fromRoot.State>,
    private progress: NgProgress,
    private dialog: DialogService,
    private ngxPermissions: NgxPermissionsService,
    private changePasswordDialogService: ChangePasswordDialogService,
    private amplitudeService: AmplitudeService
  ) {
    super()
    const locale = getLocale()
    this.currentLanguage = this.languages.find((l) => l.value === locale)
    moment.locale(locale)
  }

  ngOnInit() {
    this.isLoading$ = this.progress.ref().state.pipe(map((state) => state.active))
    this.setExpanded(this.storage.get(KEY_SIDEBAR_EXPANDED, false) === true)
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroy(this)
      )
      .subscribe(() => this.syncBodyClasses())
  }

  toggleExpanded() {
    this.setExpanded(!this.isExpanded)
    this.storage.set(KEY_SIDEBAR_EXPANDED, this.isExpanded)
    this.amplitudeService.logEvent(this.isExpanded ? 'expand-sidebar' : 'collapse-sidebar')
  }

  onFinancesClick() {
    if (!this.isExpanded) {
      this.isPaymentsMenuOpen = !this.isPaymentsMenuOpen
      return
    }
    this.router.navigate(['/invoices/list'])
  }

  async onOperationsClick() {
    if (!this.isExpanded) {
      this.isTasksMenuOpen = !this.isTasksMenuOpen
      return
    }
    const items: [string, string][] = [
      ['canSeeTasks', '/tasks/list'],
      ['canSeeIncidents', '/incidents'],
      ['canSeeInventories', '/inventories'],
      ['canSeeVendors', '/vendors'],
    ]
    for (const [permission, route] of items) {
      if (await this.ngxPermissions.hasPermission(permission)) {
        this.router.navigate([route])
        return
      }
    }
  }

  private setExpanded(expanded: boolean) {
    this.isExpanded = expanded
    if (expanded) {
      this.isPaymentsMenuOpen = false
      this.isTasksMenuOpen = false
    }
    this.syncBodyClasses()
  }

  private syncBodyClasses() {
    // main-body & top-bar-wrapper live outside this component, they offset via these body classes
    document.body.classList.toggle('sidebar-expanded', this.isExpanded)
    document.body.classList.toggle('sidebar-subnav', this.isExpanded && !!this.activeGroup)
  }

  reportsUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.reportsUrl}login?token=${token}`
  }

  getDeviceStoreLink() {
    return `${environment.appStoreUrl}/login?redirect=/details/${ProductsForPlan.SmartDevice}&token=${this.auth.token}`
  }

  goToChangelog() {
    window.open('https://changes.advance.cm/')
  }

  onMFA() {
    this.dialog.openFixedDialog(UserMfaDialogComponent, { width: '600px', height: 'auto' })
  }

  logout() {
    this.auth.logout()
  }

  onChangePassword() {
    this.changePasswordDialogService.open(this.auth.user)
  }

  switchLanguage(lang: string): void {
    const currentUrl = this.router.url.split('/').slice(1).join('/') // Remove language prefix
    window.location.href = `/${lang}/${currentUrl}`
  }
}
